/* Dental Staffing Platform — shared design tokens + primitive styles.
   Direction: "DPP" — deep forest green + mint on warm cream, editorial (Rufina serif + Lato body),
   with a persistent dark-green brand rail. Adopted from the DPP referral-app identity.
   Import once per app: `import '@dental-staffing-platform/ui/styles.css'`. Prefix: dsp-.
   Theme-aware via CSS variables; light default, dark via prefers-color-scheme + data-theme. */

:root {
  /* DPP identity: deep forest green + mint on warm cream. Editorial (Rufina serif + Lato). */
  --dsp-bg: #f7f7f2;
  --dsp-surface: #ffffff;
  --dsp-surface-2: #eef6f1;
  --dsp-border: #d9e7df;
  --dsp-text: #162522;
  --dsp-text-muted: #5f6866;
  --dsp-primary: #013a2f;
  --dsp-primary-hover: #0b4d41;
  --dsp-on-primary: #ffffff;
  --dsp-accent: #8ce2c8;
  /* Gold — the DPP signature (the logo is metallic gold). Text-safe on cream + soft tint + bright fill. */
  --dsp-gold: #a97b0f;
  --dsp-gold-soft: #f7edcf;
  --dsp-gold-bright: #f1c40f;
  --dsp-success-bg: #def8e8;
  --dsp-success-fg: #17613a;
  --dsp-warning-bg: #fff1cf;
  --dsp-warning-fg: #835a04;
  --dsp-danger-bg: #fae7e9;
  --dsp-danger-fg: #9f2d35;
  --dsp-info-bg: #e7f8f1;
  --dsp-info-fg: #013a2f;
  --dsp-neutral-bg: #edf1f2;
  --dsp-neutral-fg: #50666b;
  /* Persistent brand rail — dark green in both themes, mint active (the DPP sidebar). */
  --dsp-rail-bg: #013a2f;
  --dsp-rail-fg: #eaf6f1;
  --dsp-rail-muted: #a7d8c9;
  --dsp-rail-border: rgba(140, 226, 200, 0.22);
  --dsp-radius: 8px;
  --dsp-radius-sm: 6px;
  /* Soft elevation scale, green-tinted to sit on the cream ground. */
  --dsp-shadow-sm: 0 1px 2px rgba(1, 58, 47, 0.06), 0 1px 1px rgba(1, 58, 47, 0.04);
  --dsp-shadow: 0 1px 2px rgba(1, 58, 47, 0.05), 0 14px 34px rgba(1, 58, 47, 0.08);
  --dsp-shadow-lg: 0 2px 4px rgba(1, 58, 47, 0.06), 0 22px 46px rgba(1, 58, 47, 0.12);
  --dsp-skeleton-base: #e4efe9;
  --dsp-skeleton-sheen: #f4faf7;
  --dsp-font:
    'Lato', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial,
    sans-serif;
  --dsp-font-display: 'Rufina', Georgia, 'Times New Roman', serif;
}

/* Auto-dark ONLY when no explicit theme is set — a forced data-theme must fully win, so that
   locking light never leaves un-redefined tokens (gold-soft, success/danger bg) on dark values. */
@media (prefers-color-scheme: dark) {
  :root:not([data-theme]) {
    --dsp-bg: #0a1413;
    --dsp-surface: #10201d;
    --dsp-surface-2: #15302b;
    --dsp-border: #21403a;
    --dsp-text: #e6f2ef;
    --dsp-text-muted: #8fb0aa;
    --dsp-primary: #8ce2c8;
    --dsp-primary-hover: #a7ecd8;
    --dsp-on-primary: #05312b;
    --dsp-accent: #8ce2c8;
    --dsp-gold: #e6c04a;
    --dsp-gold-soft: #2e2a17;
    --dsp-success-bg: #06311e;
    --dsp-success-fg: #7ee0a6;
    --dsp-warning-bg: #35270a;
    --dsp-warning-fg: #f5c451;
    --dsp-danger-bg: #3a1512;
    --dsp-danger-fg: #f6a79c;
    --dsp-info-bg: #06322c;
    --dsp-info-fg: #8ce2c8;
    --dsp-neutral-bg: #1a302b;
    --dsp-neutral-fg: #b6cec8;
    --dsp-skeleton-base: #17302b;
    --dsp-skeleton-sheen: #21403a;
  }
}

:root[data-theme='light'] {
  --dsp-bg: #f7f7f2;
  --dsp-surface: #ffffff;
  --dsp-surface-2: #eef6f1;
  --dsp-border: #d9e7df;
  --dsp-text: #162522;
  --dsp-text-muted: #5f6866;
  --dsp-primary: #013a2f;
  --dsp-primary-hover: #0b4d41;
  --dsp-on-primary: #ffffff;
  --dsp-accent: #8ce2c8;
  --dsp-info-bg: #e7f8f1;
  --dsp-info-fg: #013a2f;
  --dsp-neutral-bg: #edf1f2;
  --dsp-neutral-fg: #50666b;
}

:root[data-theme='dark'] {
  --dsp-bg: #0a1413;
  --dsp-surface: #10201d;
  --dsp-surface-2: #15302b;
  --dsp-border: #21403a;
  --dsp-skeleton-base: #17302b;
  --dsp-skeleton-sheen: #21403a;
  --dsp-text: #e6f2ef;
  --dsp-text-muted: #8fb0aa;
  --dsp-primary: #8ce2c8;
  --dsp-primary-hover: #a7ecd8;
  --dsp-on-primary: #05312b;
  --dsp-accent: #8ce2c8;
  --dsp-gold: #e6c04a;
  --dsp-gold-soft: #2e2a17;
  --dsp-info-bg: #06322c;
  --dsp-info-fg: #8ce2c8;
  --dsp-neutral-bg: #1a302b;
  --dsp-neutral-fg: #b6cec8;
}

* {
  box-sizing: border-box;
}
html,
body {
  margin: 0;
  padding: 0;
}
body {
  font-family: var(--dsp-font);
  background: var(--dsp-bg);
  color: var(--dsp-text);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  line-height: 1.55;
}
a {
  color: var(--dsp-primary);
  text-decoration: none;
}
h1,
h2,
h3,
h4 {
  margin: 0;
  font-family: var(--dsp-font-display);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
  text-wrap: balance;
}

/* Buttons */
.dsp-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 1px solid transparent;
  border-radius: var(--dsp-radius-sm);
  font: inherit;
  font-weight: 650;
  cursor: pointer;
  transition:
    background 0.12s ease,
    border-color 0.12s ease,
    transform 0.08s ease,
    opacity 0.12s ease;
  white-space: nowrap;
  min-height: 44px;
}
.dsp-btn:active {
  transform: translateY(1px);
}
.dsp-btn:focus-visible {
  outline: 2px solid var(--dsp-primary);
  outline-offset: 2px;
}
.dsp-btn--md {
  padding: 0.58rem 1.05rem;
  font-size: 0.95rem;
}
.dsp-btn--sm {
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
}
.dsp-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
/* Gold is the DPP action color (their site uses gold solid CTAs); dark-green text for contrast. */
.dsp-btn--primary {
  background: var(--dsp-gold-bright);
  color: #013a2f;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.04);
}
.dsp-btn--primary:hover:not(:disabled) {
  background: #e0af0a;
}
.dsp-btn--secondary {
  background: var(--dsp-surface);
  border-color: var(--dsp-border);
  color: var(--dsp-text);
}
.dsp-btn--secondary:hover:not(:disabled) {
  background: var(--dsp-surface-2);
}
.dsp-btn--ghost {
  background: transparent;
  color: var(--dsp-text);
}
.dsp-btn--ghost:hover:not(:disabled) {
  background: var(--dsp-neutral-bg);
}
.dsp-btn--danger {
  background: var(--dsp-danger-bg);
  color: var(--dsp-danger-fg);
}

/* Inputs */
.dsp-input,
.dsp-select,
.dsp-textarea {
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--dsp-border);
  border-radius: var(--dsp-radius-sm);
  background: var(--dsp-surface);
  color: var(--dsp-text);
  font: inherit;
  font-size: 0.95rem;
  min-height: 44px;
}
.dsp-input:focus,
.dsp-select:focus,
.dsp-textarea:focus {
  outline: 2px solid var(--dsp-primary);
  outline-offset: -1px;
  border-color: var(--dsp-primary);
}
.dsp-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.dsp-field__label {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--dsp-text-muted);
}
.dsp-field__error {
  font-size: 0.8rem;
  color: var(--dsp-danger-fg);
}

/* Card */
.dsp-card {
  background: var(--dsp-surface);
  border: 1px solid var(--dsp-border);
  border-radius: var(--dsp-radius);
  box-shadow: var(--dsp-shadow);
  padding: 1.35rem;
}
.dsp-card--hover {
  transition:
    transform 0.1s ease,
    box-shadow 0.15s ease,
    border-color 0.15s ease;
}
.dsp-card--hover:hover {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--dsp-primary) 32%, var(--dsp-border));
  box-shadow: var(--dsp-shadow-lg);
}

/* Badge */
.dsp-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.18rem 0.62rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: capitalize;
}
.dsp-badge--neutral {
  background: var(--dsp-neutral-bg);
  color: var(--dsp-neutral-fg);
}
.dsp-badge--success {
  background: var(--dsp-success-bg);
  color: var(--dsp-success-fg);
}
.dsp-badge--warning {
  background: var(--dsp-warning-bg);
  color: var(--dsp-warning-fg);
}
.dsp-badge--danger {
  background: var(--dsp-danger-bg);
  color: var(--dsp-danger-fg);
}
.dsp-badge--info {
  background: var(--dsp-info-bg);
  color: var(--dsp-info-fg);
}

/* Demo markers — gold, so "not live yet" is unmistakable but on-brand. */
.dsp-demo-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.16rem 0.6rem;
  border-radius: 999px;
  background: var(--dsp-gold-soft);
  color: #7a5a06;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border: 1px solid color-mix(in srgb, var(--dsp-gold-bright) 45%, transparent);
  white-space: nowrap;
}
.dsp-demo-badge::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: var(--dsp-gold-bright);
}
.dsp-demo-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0.7rem 0.95rem;
  border-radius: var(--dsp-radius-sm);
  background: var(--dsp-gold-soft);
  border: 1px solid color-mix(in srgb, var(--dsp-gold-bright) 40%, transparent);
  color: #6b4e05;
  font-size: 0.86rem;
  margin-bottom: 1.15rem;
}
/* Availability slot toggle */
.dsp-availability-day {
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr);
  align-items: center;
  gap: 14px;
}
.dsp-availability-slots {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  min-width: 0;
}
.dsp-avail-slot {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 8px;
  border-radius: var(--dsp-radius-sm);
  border: 1px solid var(--dsp-border);
  background: var(--dsp-surface-2);
  color: var(--dsp-text-muted);
  font: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition:
    background 0.12s ease,
    border-color 0.12s ease,
    color 0.12s ease;
}
@media (max-width: 600px) {
  .dsp-availability-day {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  .dsp-avail-slot {
    padding: 10px 5px;
    font-size: 0.74rem;
  }
}
.dsp-avail-slot:hover {
  border-color: var(--dsp-primary);
}
.dsp-avail-slot:focus-visible {
  outline: 2px solid var(--dsp-primary);
  outline-offset: 2px;
}
.dsp-avail-slot--on {
  background: var(--dsp-success-bg);
  color: var(--dsp-success-fg);
  border-color: transparent;
}

/* Messages two-pane (thread list + conversation); stacks on narrow screens. */
.dsp-msg-grid {
  display: grid;
  grid-template-columns: minmax(230px, 320px) 1fr;
  gap: 14px;
  align-items: start;
}
@media (max-width: 720px) {
  .dsp-msg-grid {
    grid-template-columns: 1fr;
  }
}

/* Small gold dot appended to demo nav items. */
.dsp-navlink__demo {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: var(--dsp-gold-bright);
  margin-left: auto;
  flex: none;
}

/* Layout primitives */
.dsp-stack {
  display: flex;
  flex-direction: column;
}
.dsp-row {
  display: flex;
  align-items: center;
  min-width: 0;
}
.dsp-muted {
  color: var(--dsp-text-muted);
}
.dsp-empty {
  text-align: center;
  padding: 3.5rem 1rem;
  color: var(--dsp-text-muted);
  background: var(--dsp-surface);
  border: 1px dashed var(--dsp-border);
  border-radius: var(--dsp-radius);
}
.dsp-page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.35rem;
}
.dsp-page-header__title {
  font-size: clamp(1.65rem, 2.2vw, 2.15rem);
  font-weight: 750;
}
.dsp-page-header__subtitle {
  color: var(--dsp-text-muted);
  font-size: 0.92rem;
  margin-top: 0.25rem;
}

/* Growing-list controls. Filters stack without horizontal overflow; paging remains explicit. */
.dsp-list-toolbar {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) repeat(2, minmax(150px, 220px));
  gap: 12px;
  align-items: end;
  padding: 1rem;
  margin-bottom: 1rem;
  background: var(--dsp-surface);
  border: 1px solid var(--dsp-border);
  border-radius: var(--dsp-radius);
  box-shadow: var(--dsp-shadow-sm);
}
.dsp-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding: 12px 0;
}
.dsp-pagination__summary,
.dsp-pagination__page {
  color: var(--dsp-text-muted);
  font-size: 0.84rem;
}
.dsp-pagination__page {
  min-width: 82px;
  text-align: center;
}
@media (max-width: 720px) {
  .dsp-list-toolbar {
    grid-template-columns: 1fr;
  }
  .dsp-pagination {
    align-items: stretch;
    flex-direction: column;
  }
  .dsp-pagination > .dsp-row {
    justify-content: space-between;
  }
}

/* Spinner */
.dsp-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid var(--dsp-border);
  border-top-color: var(--dsp-primary);
  border-radius: 50%;
  animation: dsp-spin 0.7s linear infinite;
}
@keyframes dsp-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Skeleton (shimmer placeholder — prefer over spinners for content > ~1s) */
.dsp-skeleton {
  display: block;
  background: linear-gradient(
    100deg,
    var(--dsp-skeleton-base) 30%,
    var(--dsp-skeleton-sheen) 50%,
    var(--dsp-skeleton-base) 70%
  );
  background-size: 200% 100%;
  animation: dsp-shimmer 1.3s ease-in-out infinite;
}
@keyframes dsp-shimmer {
  to {
    background-position: -200% 0;
  }
}

/* Brand mark — a real logo lockup replaces the emoji: icon tile + wordmark */
.dsp-brandmark {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}
.dsp-brandmark__tile {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: linear-gradient(150deg, var(--dsp-primary), var(--dsp-primary-hover));
  color: var(--dsp-on-primary);
  box-shadow: var(--dsp-shadow-sm);
  flex: none;
}
.dsp-brandmark__word {
  font-weight: 800;
  font-size: 1.02rem;
  letter-spacing: -0.02em;
  color: var(--dsp-text);
}
/* The real DPP gold wordmark. Transparent PNG — reads on the dark rail and on light login. */
.dsp-rail-logo {
  display: block;
  width: 100%;
  max-width: 190px;
  height: auto;
}
.dsp-login-logo {
  display: block;
  width: min(260px, 78%);
  height: auto;
  margin: 0 auto;
}

/* Header user chip */
.dsp-userchip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.28rem 0.4rem 0.28rem 0.28rem;
  border: 1px solid var(--dsp-border);
  border-radius: 999px;
  background: var(--dsp-surface);
}
.dsp-userchip__avatar {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background: var(--dsp-neutral-bg);
  color: var(--dsp-primary);
  font-size: 0.72rem;
  font-weight: 800;
  flex: none;
}
.dsp-userchip__name {
  font-size: 0.84rem;
  font-weight: 650;
  padding-right: 0.35rem;
}

/* Stat tile (dashboard) */
.dsp-stat__icon {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: var(--dsp-neutral-bg);
  color: var(--dsp-primary);
  margin-bottom: 0.75rem;
}
.dsp-stat__icon--accent {
  background: var(--dsp-gold-soft);
  color: var(--dsp-gold);
}

/* App shell */
.dsp-shell {
  display: grid;
  grid-template-columns: 248px 1fr;
  min-height: 100vh;
}
.dsp-shell__sidebar {
  background: var(--dsp-rail-bg);
  border-right: 1px solid var(--dsp-rail-border);
  padding: 1rem 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  /* Pin the rail to the viewport so it never stretches with long content; scroll internally if tall. */
  position: sticky;
  top: 0;
  align-self: start;
  height: 100vh;
  overflow-y: auto;
}
.dsp-shell__brand {
  padding: 0.5rem 0.6rem 0.9rem;
}
.dsp-mobile-brand-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.dsp-mobile-menu-trigger {
  display: none;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  min-width: 88px;
  min-height: 44px;
  padding: 0.55rem 0.75rem;
  border: 1px solid var(--dsp-rail-border);
  border-radius: var(--dsp-radius-sm);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font: inherit;
  font-weight: 650;
  cursor: pointer;
}
.dsp-mobile-menu-trigger:hover,
.dsp-mobile-menu-trigger:focus-visible {
  background: rgba(255, 255, 255, 0.14);
}
.dsp-mobile-signout {
  display: none;
}
.dsp-shell__main {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.dsp-shell__header {
  height: 60px;
  border-bottom: 1px solid var(--dsp-border);
  background: color-mix(in srgb, var(--dsp-surface) 88%, transparent);
  backdrop-filter: saturate(1.4) blur(6px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  gap: 1rem;
  position: sticky;
  top: 0;
  z-index: 5;
}
.dsp-shell__content {
  padding: 2rem clamp(1.25rem, 3vw, 3rem);
  max-width: 1600px;
  width: 100%;
  margin: 0 auto;
}
.dsp-navlink {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0.7rem;
  border-radius: var(--dsp-radius-sm);
  color: var(--dsp-text);
  font-size: 1rem;
  font-weight: 550;
  min-height: 44px;
  transition:
    background 0.12s ease,
    color 0.12s ease;
}
.dsp-navlink svg {
  color: var(--dsp-text-muted);
  flex: none;
  transition: color 0.12s ease;
}
.dsp-navlink:hover {
  background: var(--dsp-neutral-bg);
}
.dsp-navlink:hover svg {
  color: var(--dsp-primary);
}
.dsp-navlink--active {
  background: var(--dsp-gold-bright);
  color: #013a2f;
  box-shadow: var(--dsp-shadow-sm);
}
.dsp-navlink--active svg {
  color: #013a2f;
}
.dsp-nav-section {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--dsp-text-muted);
  padding: 0.9rem 0.7rem 0.35rem;
}
.dsp-shell__sidebar-spacer {
  flex: 1;
}
.dsp-shell__sidebar-footer {
  border-top: 1px solid var(--dsp-rail-border);
  padding-top: 0.6rem;
  margin-top: 0.4rem;
}

/* Branded rail: the sidebar is dark green in both themes, so its contents run light. */
.dsp-shell__sidebar .dsp-brandmark__word {
  color: #ffffff;
}
.dsp-shell__sidebar .dsp-brandmark__tile {
  background: var(--dsp-accent);
  color: var(--dsp-primary);
}
.dsp-shell__sidebar .dsp-navlink {
  color: var(--dsp-rail-fg);
}
.dsp-shell__sidebar .dsp-navlink svg {
  color: var(--dsp-rail-muted);
}
.dsp-shell__sidebar .dsp-navlink:hover {
  background: rgba(255, 255, 255, 0.08);
}
.dsp-shell__sidebar .dsp-navlink:hover svg {
  color: #ffffff;
}
.dsp-shell__sidebar .dsp-navlink--active,
.dsp-shell__sidebar .dsp-navlink--active:hover {
  background: var(--dsp-gold-bright);
  color: #013a2f;
}
.dsp-shell__sidebar .dsp-navlink--active svg {
  color: #013a2f;
}
.dsp-shell__sidebar .dsp-nav-section {
  color: var(--dsp-rail-muted);
}
.dsp-shell__sidebar .dsp-userchip {
  background: rgba(255, 255, 255, 0.06);
  border-color: var(--dsp-rail-border);
}
.dsp-shell__sidebar .dsp-userchip__name {
  color: #ffffff;
}
.dsp-shell__sidebar .dsp-userchip__avatar {
  background: var(--dsp-accent);
  color: var(--dsp-primary);
}
.dsp-shell__sidebar .dsp-btn--ghost {
  color: var(--dsp-rail-fg);
}
.dsp-shell__sidebar .dsp-btn--ghost:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
}

@media (max-width: 720px) {
  .dsp-shell {
    grid-template-columns: 1fr;
    grid-template-rows: auto minmax(0, 1fr);
  }
  .dsp-shell__sidebar {
    position: relative;
    height: auto;
    overflow-y: visible;
    flex-direction: column;
    align-items: stretch;
    overflow-x: visible;
    border-right: none;
    border-bottom: 1px solid var(--dsp-rail-border);
    padding: 0.5rem;
    gap: 0.4rem;
  }
  .dsp-shell__brand {
    padding: 0.25rem;
  }
  .dsp-shell__brand .dsp-rail-logo {
    max-width: 104px;
  }
  .dsp-mobile-menu-trigger {
    display: inline-flex;
  }
  .dsp-shell__sidebar > .dsp-mobile-nav {
    display: none;
  }
  .dsp-shell__sidebar > .dsp-mobile-nav--open {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    width: auto;
    max-height: min(70vh, 560px);
    overflow-y: auto;
    padding: 0.75rem;
    background: var(--dsp-rail-bg);
    border: 1px solid var(--dsp-rail-border);
    box-shadow: var(--dsp-shadow-lg);
    z-index: 20;
  }
  .dsp-shell__sidebar .dsp-mobile-signout {
    display: inline-flex;
    justify-content: flex-start;
    min-height: 44px;
    margin-top: 0.25rem;
    border-top: 1px solid var(--dsp-rail-border);
  }
  .dsp-navlink {
    white-space: nowrap;
  }
  /* Section labels, the flex spacer and the footer only make sense in the vertical rail. */
  .dsp-nav-section,
  .dsp-shell__sidebar-spacer,
  .dsp-shell__sidebar-footer {
    display: none;
  }
  .dsp-shell__header {
    height: 56px;
    padding: 0 0.75rem;
  }
  .dsp-shell__content {
    padding: 1.25rem 1rem;
  }
  .dsp-shell__content > * {
    min-width: 0;
  }
  .dsp-page-header {
    flex-direction: column;
  }
  .dsp-page-header > * {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.001ms !important;
    transition-duration: 0.001ms !important;
  }
}

.dsp-skip-link {
  position: fixed;
  top: 8px;
  left: 8px;
  z-index: 100;
  transform: translateY(-160%);
  background: var(--dsp-surface);
  border: 2px solid var(--dsp-primary);
  border-radius: 6px;
  padding: 12px;
}
.dsp-skip-link:focus {
  transform: translateY(0);
}
